// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import NonVisualFieldLegend from "../../../NonVisualFieldLegend/NonVisualFieldLegend";
import Alert from "src/components/shared/alerts/Alert/Alert";
import TumblingEGraph from "./TumblingEGraph/TumblingEGraph";
import LandoltCGraph from "./LandoltCGraph/LandoltCGraph";
import SnellenGraph from "./SnellenGraph/SnellenGraph";
import BaileyLovieGraph from "./BaileyLovieGraph/BaileyLovieGraph";
import ETDRSGraph from "./ETDRSGraph/ETDRSGraph";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Constants
import { algorithmTypes, examCardEnums } from "src/constants/enums";

// Utils
import { isTerminatedExamReport } from "src/utils/exams/examReportsHelper";

const VisualAcuityGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.VISUAL_ACUITY.sectionName,
      }),
    [exam, examReport]
  );

  const examAlgorithm = useMemo(
    () => examReport?.exam?.algorithm,
    [examReport?.exam?.algorithm]
  );

  const isError = useMemo(
    () => !examReport?.state?.charts || !examReport?.state?.charts?.length,
    [examReport?.state?.charts]
  );

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "20px",
          }}
        >
          {isTerminated && (
            <Alert
              variant="error"
              alertContent={t("Terminated Exam")}
              props={{ width: "100%" }}
            />
          )}

          {!isError && <NonVisualFieldLegend />}

          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
            }}
          >
            {examAlgorithm === algorithmTypes.TUMBLING_E && (
              <TumblingEGraph examData={examReport} />
            )}
            {examAlgorithm === algorithmTypes.LANDOLT_C && (
              <LandoltCGraph examData={examReport} />
            )}
            {examAlgorithm === algorithmTypes.SNELLEN && (
              <SnellenGraph examData={examReport} />
            )}
            {examAlgorithm === algorithmTypes.BAILEY_LOVIE && (
              <BaileyLovieGraph examData={examReport} />
            )}
            {examAlgorithm === algorithmTypes.ETDRS && (
              <ETDRSGraph examData={examReport} />
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default VisualAcuityGraph;
